import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  Platform,
  Animated,
  TouchableWithoutFeedback,
  ScrollView,
} from 'react-native';
import {
  getCurrentDate,
  getOneMonthBackDate,
} from '../components/common/utils/date-time-conversions';
import { ChargerAttributes } from '../models/charger-model';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { isSuccessful } from '../services/api-requests';
import { getMyChargers } from '../store/actions/charging-actions';
import { notificationService } from '../services/notification-service';
import MyChargerInfoCard from './MyChargerInfoCard';
import DateFilterModal from '../components/modals/DateFilterModal';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { getMyChargerHistory } from '../services/charger-history-service';
import { ChargerHistoryAttributes } from '../models/charger-history-model';
import MyChargerHistoryAtAGlance from './MyChargingHistoryAtAGlance';
import ChargeHistoryCard from './ChargeHistoryCard';
import { AppDispatch } from '../store';
import PaginationComponent from '../components/PaginationComponent';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getDeviceDimensions } from '../components/common/utils/platform.util';
import { useHostChargerSparklineData } from '../hooks/useHostChargerSparklineData';
import { prefetchChargerThumbnails } from '../services/charger-image-cache-service';

// Extend dayjs with plugins
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

const { deviceHeight } = getDeviceDimensions();

interface MyChargersProps {
  navigation: any;
  route: any;
}

export default function MyChargers({ navigation, route }: MyChargersProps) {
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [customDates, setCustomDates] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: getOneMonthBackDate(),
    end: getCurrentDate(),
  });
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalKwh, setTotalKwh] = useState(0);
  const [dailyRevenueData, setDailyRevenueData] = useState<Array<{ date: string; revenue: number }>>([]);
  const myChargersStore: ChargerAttributes[] = useSelector(
    (state: RootState) => state.chargers.myChargers
  );

  const dispatch: AppDispatch = useDispatch();

  const [filteredChargers, setFilteredChargers] = React.useState<
    ChargerHistoryAttributes[]
  >([]);
  const [myChargerHistory, setMyChargerHistory] = React.useState<
    ChargerHistoryAttributes[]
  >([]);

  const [view, setView] = useState<'list' | 'history'>('list');

  // When a user filters by charger
  const [chargeHistoryFilter, setChargeHistoryFilter] =
    React.useState<string>('');

  //Show History current page
  const [currentPageSh, setCurrentPageSh] = useState(1);

  //Charger List current page
  const [currentPageCl, setCurrentPageCl] = useState(1);
  const scrollY = useRef(new Animated.Value(0)).current;

  // Fetch sparkline data for host chargers
  const { sparklineData, loading: sparklineLoading } = useHostChargerSparklineData({
    chargers: myChargersStore,
    enabled: true,
  });

  const itemsPerPage = 10;

  const handlePageChange = (page: number) => {
    if (view === 'list') {
      setCurrentPageCl(page); // Update current page for Charger List
    } else if (view === 'history') {
      setCurrentPageSh(page); // Update current page for Show History
    }
  };

  // Animation for shrinking the top section
  const shrinkAnimation = scrollY.interpolate({
    inputRange: [0, 300],
    outputRange: [350, 200],
    extrapolate: 'clamp',
  });
  const textScaleAnimation = scrollY.interpolate({
    inputRange: [0, 300],
    outputRange: [1, 0.8], // Scale text dynamically
    extrapolate: 'clamp',
  });

  useEffect(() => {
    scrollY.addListener(({ value }) => {
      console.log('ScrollY Value:', value);
    });
  }, []);

  const handleSetSelectedOption = (option: any) => {
    setSelectedOption(option);
  };

  const historySumWithDateRange = (history: ChargerHistoryAttributes[]) => {
    let total_charge_price = 0;
    let total_kwh_calc = 0;

    history.forEach((h) => {
      const start = dayjs(h.charging_start_time);
      const end = dayjs(h.charging_end_time);
      
      // Parse the start date - handle both MM/DD/YYYY and YYYY-MM-DD formats
      let rangeStart;
      if (customDates.start && customDates.start.includes('/')) {
        rangeStart = dayjs(customDates.start, 'MM/DD/YYYY');
      } else {
        rangeStart = dayjs(customDates.start, 'YYYY-MM-DD');
      }
      
      // Parse the end date - handle both MM/DD/YYYY and YYYY-MM-DD formats
      let rangeEnd;
      if (customDates.end && customDates.end.includes('/')) {
        rangeEnd = dayjs(customDates.end, 'MM/DD/YYYY').add(1, 'day').startOf('day');
      } else {
        rangeEnd = dayjs(customDates.end, 'YYYY-MM-DD').add(1, 'day').startOf('day');
      }

      // Check if the charger's date is within the range
      if (
        start.isBetween(rangeStart, rangeEnd, null, '[]') &&
        end.isBetween(rangeStart, rangeEnd, null, '[]')
      ) {
        total_charge_price += h.total_charge_price ?? 0;
        total_kwh_calc += h.total_kwh ?? 0;
      }
    });

    return {
      total_charge_price: total_charge_price.toFixed(2),
      total_kwh_calc: total_kwh_calc.toFixed(1),
    };
  };

  const fetchAndFormatChargerData = async (chargerId?: string) => {
    try {
      // Fetch charger histories
      const myChargerHistoriesReq = await getMyChargerHistory();
      let myChargerHistoriesData;
      if (isSuccessful(myChargerHistoriesReq.status)) {
        myChargerHistoriesData = myChargerHistoriesReq.payload;
      }
      if (myChargerHistoriesData) {
        const { total_charge_price, total_kwh_calc } = historySumWithDateRange(
          myChargerHistoriesData
        );
        const formattedChargers = myChargerHistoriesData
          .filter((h) => {
            if (chargerId && chargerId !== '') {
              return h.chargerId === chargerId;
            } else {
              //If no chargerId is supplied, just show all
              return true;
            }
          })
          .sort(
            (a: ChargerHistoryAttributes, b: ChargerHistoryAttributes) =>
              new Date(b.charging_start_time ?? 0).getTime() -
              new Date(a.charging_start_time ?? 0).getTime()
          )
          .map((history) => {
            // Format updatedAt date
            const isoDateString = String(history.charging_start_time);
            const date = new Date(isoDateString);
            const formattedDate = `${(date.getMonth() + 1)
              .toString()
              .padStart(2, '0')}/${date
              .getDate()
              .toString()
              .padStart(2, '0')}/${date.getFullYear()}`;

            return {
              ...history,
              updatedAt: date,
              formattedDate,
            };
          });
        setTotalKwh(Number(total_kwh_calc));
        setTotalRevenue(Number(total_charge_price));
        setMyChargerHistory(formattedChargers);
      }
    } catch (e) {
      console.error('Error getting my charger histories', e);
    }
  };

  // Pagination Logic
  const [currentItemsCh, setCurrentItemsCh] = React.useState<
    ChargerHistoryAttributes[]
  >([]);
  const [currentItemsChargers, setCurrentItemsChargers] = React.useState<
    ChargerAttributes[]
  >([]);

  // Calculate charger associations
  const chargerAssociation: ChargerAttributes[] = useMemo(() => {
    if (myChargerHistory && myChargerHistory.length > 0 && myChargersStore) {
      return myChargerHistory
        .map((ch) => myChargersStore.find((c) => c.uuid === ch.chargerId))
        .filter(
          (matchedCharger): matchedCharger is ChargerAttributes =>
            !!matchedCharger
        );
    }
    return [];
  }, [myChargerHistory, myChargersStore]);

  useEffect(() => {
    const indexOfLastItemCl = currentPageCl * itemsPerPage;
    const indexOfFirstItemCl = indexOfLastItemCl - itemsPerPage;
    const indexOfLastItemSh = currentPageSh * itemsPerPage;
    const indexOfFirstItemSh = indexOfLastItemSh - itemsPerPage;

    if (myChargerHistory) {
      const filteredChargerHistory = filterByDateRange(
        customDates.start,
        customDates.end
      );
      setCurrentItemsCh(
        filteredChargerHistory.slice(indexOfFirstItemSh, indexOfLastItemSh)
      );
    }
    if (myChargersStore && myChargersStore.length > 0) {
      const slicedChargers = myChargersStore.slice(
        indexOfFirstItemCl,
        indexOfLastItemCl
      );
      setCurrentItemsChargers(slicedChargers);
    } else {
      setCurrentItemsChargers([]);
    }
  }, [
    currentPageCl,
    currentPageSh,
    myChargersStore,
    myChargerHistory,
    customDates,
  ]);

  const filterByDateRange = (startDate: any, endDate: any) => {
    return myChargerHistory.filter((item) => {
      // Parse the datetime string from API
      const chargingStartTime = dayjs(item.charging_start_time);
      
      // Parse the start date string - handle both MM/DD/YYYY and YYYY-MM-DD formats
      let start;
      if (startDate.includes('/')) {
        start = dayjs(startDate, 'MM/DD/YYYY');
      } else {
        start = dayjs(startDate, 'YYYY-MM-DD');
      }
      
      // Parse the end date string - handle both MM/DD/YYYY and YYYY-MM-DD formats
      let end;
      if (endDate.includes('/')) {
        end = dayjs(endDate, 'MM/DD/YYYY').add(1, 'day').startOf('day');
      } else {
        end = dayjs(endDate, 'YYYY-MM-DD').add(1, 'day').startOf('day');
      }

      return (
        chargingStartTime.isSameOrAfter(start) &&
        chargingStartTime.isBefore(end)
      );
    });
  };

  React.useEffect(() => {
    const getChargerData = async () => {
      await fetchAndFormatChargerData();
      dispatch(getMyChargers());
    };

    getChargerData();
  }, []);

  // Prefetch charger thumbnails for better performance
  React.useEffect(() => {
    if (myChargersStore && myChargersStore.length > 0) {
      const chargerIds = myChargersStore.map(c => c.uuid).filter(Boolean) as string[];
      // Prefetch in background (fire and forget)
      prefetchChargerThumbnails(chargerIds, 3).catch(console.error);
    }
  }, [myChargersStore]);

  React.useEffect(() => {
    // Automatically calculate totals and apply filters when dates or history change
    const applyFilters = () => {
      const filteredData = filterByDateRange(
        customDates.start,
        customDates.end
      );
      const { total_charge_price, total_kwh_calc } =
        historySumWithDateRange(filteredData);

      setFilteredChargers(filteredData);
      setTotalKwh(Number(total_kwh_calc));
      setTotalRevenue(Number(total_charge_price));

      // Calculate daily revenue data
      const revenueByDate = new Map<string, number>();
      const start = dayjs(customDates.start);
      const end = dayjs(customDates.end);
      
      // Initialize all dates with 0
      let current = start;
      while (current.isBefore(end) || current.isSame(end, 'day')) {
        revenueByDate.set(current.format('YYYY-MM-DD'), 0);
        current = current.add(1, 'day');
      }

      // Aggregate revenue by day
      filteredData.forEach(h => {
        const dateRaw = h?.charging_end_time;
        if (!dateRaw) return;
        const d = dayjs(dateRaw);
        const key = d.format('YYYY-MM-DD');
        
        if (revenueByDate.has(key)) {
          const revenue = Number(h?.total_charge_price) || 0;
          revenueByDate.set(key, (revenueByDate.get(key) || 0) + revenue);
        }
      });

      // Convert to array
      const dailyRevenue = Array.from(revenueByDate.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, revenue]) => ({ date, revenue }));

      setDailyRevenueData(dailyRevenue);
    };

    applyFilters();
  }, [customDates, myChargerHistory]);

  const filterDate = (setDates: any) => {
    setCustomDates({ start: setDates.start, end: setDates.end });
    let filteredChargerList;

    filteredChargerList = filterByDateRange(setDates.start, setDates.end);

    // Update state with filtered data
    setFilteredChargers(filteredChargerList);

    setFilterModalVisible(!filterModalVisible);
    notificationService.sendNotification('success', `Date Filter Applied`);
  };

  const handleCancel = () => {
    setFilterModalVisible(!filterModalVisible);
  };

  const handleShowHistoryPress = async (chargerId: string) => {
    const matchedChargerName = myChargersStore?.find(
      (c) => c.uuid === chargerId
    );
    setChargeHistoryFilter(matchedChargerName?.name ?? '');
    if (chargerId !== '') {
      //set the current page on the filter to 1
      setCurrentPageSh(1);
    }
    await fetchAndFormatChargerData(chargerId);
    setView('history');
  };

  const handleChargerListPress = async () => {
    setChargeHistoryFilter('');
    setView('list');
  };

  const handleClearFilter = async () => {
    setChargeHistoryFilter('');
    await fetchAndFormatChargerData('');
  };

  const callGetMyChargers = route?.params?.state;

  const handleFilter = () => {
    setFilterModalVisible(!filterModalVisible);
  };

  const registerCharger = () => {
    navigation.navigate('ChargerValidation');
  };

  const editCharger = (chargerDetails: any) => {
    navigation.navigate('RegisterUpdateCharger', {
      chargerDetails: chargerDetails,
      editCharger: true,
      state: callGetMyChargers,
    });
  };

  const handleCustomDatePicker = (
    date: string,
    pickerType: 'start' | 'end'
  ) => {
    setCustomDates((prev) => ({ ...prev, [pickerType]: date }));
  };

  const handleInsightsPress = () => {
    // Select all chargers when navigating to insights from main button
    const allChargerIds = myChargersStore.map(c => c.uuid).filter(Boolean) as string[];
    navigation.navigate('HostInsights' as never, { preselectedChargerIds: allChargerIds } as never);
  };

  const handleChargerInsightsPress = (chargerId: string) => {
    navigation.navigate('HostInsights' as never, { preselectedChargerIds: [chargerId] } as never);
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* Static Top Section */}
      <View style={styles.staticTop}>
        <MyChargerHistoryAtAGlance
          handleFilter={handleFilter}
          customDates={customDates}
          filteredChargers={filteredChargers}
          totalKwh={totalKwh}
          totalRevenue={totalRevenue}
          dailyRevenueData={dailyRevenueData}
          onInsightsPress={handleInsightsPress}
        />

        <View style={styles.line} />

        <View style={styles.selectionContainer}>
          <Pressable
            onPress={handleChargerListPress}
            style={styles.chargerHistoryListButtonContainer}
          >
            <Text
              style={[
                styles.selectionText,
                view === 'list'
                  ? styles.activeSelection
                  : styles.inactiveSelection,
              ]}
            >
              Charger List
            </Text>
          </Pressable>
          <Pressable
            onPress={() => handleShowHistoryPress('')}
            style={styles.chargerHistoryListButtonContainer}
          >
            <Text
              style={[
                styles.selectionText,
                view === 'history'
                  ? styles.activeSelection
                  : styles.inactiveSelection,
              ]}
            >
              Show History
            </Text>
          </Pressable>
          <Pressable
            onPress={registerCharger}
            style={styles.registerButtonContainer}
          >
            <Text style={styles.registerButton}>Register a Charger</Text>
          </Pressable>
        </View>

        {chargeHistoryFilter !== '' && (
          <View style={styles.filterContainer}>
            <Pressable onPress={handleClearFilter} style={styles.clearButton}>
              <Text style={styles.clearButtonText}>X</Text>
            </Pressable>
            <Text style={styles.filterText}>
              {`Currently filtering ${chargeHistoryFilter}`}
            </Text>
          </View>
        )}
      </View>

      {/* Scrollable List Section */}
      <View style={styles.scrollContainer}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={true}
          contentContainerStyle={{ paddingBottom: 20 }}
        >
          <View style={styles.listContainer}>
            {view === 'list' && currentItemsChargers.length > 0 ? (
              currentItemsChargers.map((data, index) => {
                const getSparklineData = () => {
                  if (sparklineData && data.uuid && sparklineData[data.uuid]) {
                    return sparklineData[data.uuid];
                  }
                  
                  // Fallback: create 7 days of zero data
                  const endDate = new Date();
                  return Array.from({ length: 7 }, (_, index) => {
                    const date = new Date(endDate);
                    date.setDate(date.getDate() - (6 - index));
                    return {
                      day: date.toISOString().split('T')[0],
                      totalKwh: 0,
                    };
                  });
                };

                return (
                  <React.Fragment key={`chargerInfoCardKey${index}`}>
                    <MyChargerInfoCard
                      index={index}
                      data={data}
                      editCharger={editCharger}
                      showHistory={handleShowHistoryPress}
                      navigation={navigation}
                      sparklineData={getSparklineData()}
                      onInsightsPress={handleChargerInsightsPress}
                      key={`chargerInfoCard${index}`}
                    />
                    {index < currentItemsChargers.length - 1 && (
                      <View style={styles.line} />
                    )}
                  </React.Fragment>
                );
              })
            ) : view === 'history' && currentItemsCh.length > 0 ? (
              currentItemsCh.map((ch, index) => (
                <React.Fragment key={`chargerHistoryCardKey${index}`}>
                  <ChargeHistoryCard
                    index={index}
                    chargerHistory={ch}
                    charger={chargerAssociation[index]}
                    key={`chargeHistoryCard${index}`}
                    isPressable={false}
                    showPaidOn={true}
                    viewContext="host"
                  />
                  {index < currentItemsChargers.length - 1 && (
                    <View style={styles.line} />
                  )}
                </React.Fragment>
              ))
            ) : (
              <Text style={styles.noItemsText}>No items to display.</Text>
            )}
          </View>
        </ScrollView>
      </View>

      {/* Pagination */}
      {(view === 'list'
        ? currentItemsChargers.length > 0
        : currentItemsCh.length > 0) && (
        <View style={styles.paginationContainer}>
          <PaginationComponent
            currentPage={view === 'list' ? currentPageCl : currentPageSh}
            totalPages={
              view === 'list'
                ? Math.ceil(myChargersStore.length / itemsPerPage)
                : Math.ceil(
                    filterByDateRange(customDates.start, customDates.end)
                      .length / itemsPerPage
                  )
            }
            handlePageChange={handlePageChange}
          />
        </View>
      )}

      {/* modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={filterModalVisible}
        onRequestClose={() => {
          setFilterModalVisible(!filterModalVisible);
        }}
      >
        <TouchableWithoutFeedback
          // Trigger "cancel" when clicking outside modal
          onPress={handleCancel}
        >
          <DateFilterModal
            selectedOption={selectedOption}
            setSelectedOption={handleSetSelectedOption}
            filterDate={filterDate}
            handleCancel={handleCancel}
            customDates={customDates}
          />
        </TouchableWithoutFeedback>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    // Ensure enough padding for pagination
    paddingBottom: 100,
  },
  safeArea: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  staticTop: {
    backgroundColor: '#1A1D21',
    padding: 10,
    zIndex: 10,
  },
  scrollContainer: {
    flex: 1,
  },
  line: Platform.select({
    web: {
      borderBottomColor: '#636363',
      borderBottomWidth: 1,
      borderStyle: 'dashed',
      marginVertical: 20,
    },
    default: {
      borderBottomColor: '#636363',
      borderBottomWidth: 1,
      marginTop: 20,
      marginBottom: 10,
    },
  }),
  selectionContainer: Platform.select({
    web: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-around',
    },
    default: {
      height: 50,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-around',
    },
  }),
  selectionText: {
    color: '#fff',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Bold',
  },
  activeSelection: {
    color: 'white',
  },
  inactiveSelection: {
    color: '#aaa',
  },
  chargerHistoryListButtonContainer: {
    borderRadius: 23,
    borderColor: '#FFF',
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  registerButtonContainer: {
    borderRadius: 23,
    backgroundColor: '#39BEB780',
    paddingVertical: 10,
    paddingHorizontal: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  registerButton: {
    color: '#fff',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Bold',
  },
  filterContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: 'white',
    borderRadius: 9999,
  },
  clearButton: {
    backgroundColor: 'white',
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  clearButtonText: {
    color: 'black',
    fontWeight: 'bold',
  },
  filterText: Platform.select({
    web: {
      color: 'white',
      fontSize: 14,
      fontFamily: 'PlusJakartaSans-Bold',
    },
    default: {
      flex: 1,
      color: 'white',
      fontSize: 14,
      fontFamily: 'PlusJakartaSans-Bold',
    },
  }),
  listContainer: {
    paddingHorizontal: 16,
  },
  noHistoryText: {
    color: 'white',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 20,
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 10,
  },
  activePage: {
    color: 'white',
    marginHorizontal: 8,
    fontWeight: 'bold',
  },
  inactivePage: {
    color: '#aaa',
    marginHorizontal: 8,
  },
  noItemsText: {
    color: 'white',
    textAlign: 'center',
    marginVertical: 20,
  },
});
